import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { StatusSolicitacaoBadge } from "@/components/status-badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ApiError, apiFetch } from "@/lib/api";
import { formatarDataHora } from "@/lib/format";
import type { Perfil, SolicitacaoListada } from "@/lib/types";

export function SolicitacoesPage() {
  const qc = useQueryClient();
  const [aprovando, setAprovando] = useState<SolicitacaoListada | null>(null);
  const [perfilEscolhido, setPerfilEscolhido] = useState<Perfil>("user");
  const [recusando, setRecusando] = useState<SolicitacaoListada | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["solicitacoes"],
    queryFn: () => apiFetch<SolicitacaoListada[]>("/api/solicitacoes"),
  });

  const aprovar = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/solicitacoes/${id}/aprovar`, { method: "POST", body: { perfil: perfilEscolhido } }),
    onSuccess: () => {
      toast.success("Solicitação aprovada — convite enviado");
      qc.invalidateQueries({ queryKey: ["solicitacoes"] });
      qc.invalidateQueries({ queryKey: ["convites"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "stats"] });
      setAprovando(null);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Falha ao aprovar"),
  });

  const recusar = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/solicitacoes/${id}/recusar`, { method: "POST" }),
    onSuccess: () => {
      toast.success("Solicitação recusada");
      qc.invalidateQueries({ queryKey: ["solicitacoes"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "stats"] });
      setRecusando(null);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Falha ao recusar"),
  });

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-semibold">Solicitações de acesso</h1>
        <p className="text-sm text-muted-foreground">
          Aprovar ou recusar pedidos enviados pelo formulário público <code>/solicitar-acesso</code>.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Fila de solicitações</CardTitle>
          <CardDescription>{data ? `${data.length} solicitação(ões)` : "Carregando..."}</CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>E-mail</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Instagram</TableHead>
                <TableHead>Origem</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Recebida</TableHead>
                <TableHead className="w-32">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : data && data.length > 0 ? (
                data.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.email}</TableCell>
                    <TableCell>{s.name ?? "—"}</TableCell>
                    <TableCell>{s.instagramUsername ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{s.source ?? "—"}</TableCell>
                    <TableCell>
                      <StatusSolicitacaoBadge status={s.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatarDataHora(s.criadoEm)}</TableCell>
                    <TableCell>
                      {s.status === "pending" && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-success hover:text-success"
                            onClick={() => {
                              setPerfilEscolhido("user");
                              setAprovando(s);
                            }}
                            title="Aprovar"
                          >
                            <Check className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => setRecusando(s)}
                            title="Recusar"
                          >
                            <X className="size-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                    Nenhuma solicitação ainda.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={aprovando !== null} onOpenChange={(o) => !o && setAprovando(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprovar solicitação</DialogTitle>
            <DialogDescription>
              Escolha o perfil e enviaremos um convite para <strong>{aprovando?.email}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="perfil-aprov">Perfil</Label>
            <Select value={perfilEscolhido} onValueChange={(v) => setPerfilEscolhido(v as Perfil)}>
              <SelectTrigger id="perfil-aprov">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Usuário</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button onClick={() => aprovando && aprovar.mutate(aprovando.id)} disabled={aprovar.isPending}>
              {aprovar.isPending ? "Aprovando..." : "Aprovar e convidar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={recusando !== null} onOpenChange={(o) => !o && setRecusando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recusar solicitação?</AlertDialogTitle>
            <AlertDialogDescription>
              A solicitação de <strong>{recusando?.email}</strong> será marcada como recusada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => recusando && recusar.mutate(recusando.id)}
            >
              Recusar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
